<template>
    <hox-notification-item
        :show-button="notification.downloadLink !== undefined"
        :show-clear-button="showClearButton"
        :show-error-message="hasFailed"
        :show-abort-button="showAbortButton"
        :status="notification.status"
        :notification="notification"
        :is-job-finished="isJobFinished"
        @clear="removeNotification"
        @notificationDeleted="deleteCompletedNotification"
    >
        <template #title>
            <span v-if="notification.job.campaignDetails && notification.job.clientDetails">
                Client:
                <strong>{{ notification.job.clientDetails?.name }}</strong>
                <br />
                Campaign:
                <strong>{{ notification.job.campaignDetails?.name }}</strong>
            </span>
            {{ title }}
        </template>
        <template #subtitle>
            {{ statusText }}
        </template>
        <template #content>
            <template v-if="isCompleted">
                <p>Here is the link to download. We have also sent you a link via email.</p>
                <template v-if="failedLabels.length">
                    <p>The following ads could not be generated:</p>
                    <ul>
                        <li v-for="label in failedLabels" :key="label">{{ label }}</li>
                    </ul>
                </template>
            </template>
            <template v-else-if="!hasFailed">
                <p v-if="isFacebookJob">
                    Your video(s) are processing, and will be pushed directly to Facebook Ads Manager.
                </p>
                <div v-if="subProgress" class="download-job-notification-item__sub-progress">
                    <div v-for="(deliverable, hash) in subProgress" :key="hash">
                        <div class="download-job-notification-item__sub-progress-label">
                            <p>{{ deliverable.label }}</p>
                            <p>{{ deliverable.percent }}%</p>
                        </div>
                        <hox-progress-bar
                            :is-animated="true"
                            :progress-percentage="deliverable.percent"
                            type="in-progress"
                        />
                    </div>
                    <p v-if="invisibleProgressCount > 0">Plus {{ invisibleProgressCount }} more in progress...</p>
                </div>
            </template>
            <div>
                <p v-if="notification.job.payload.startedAt">Started on: {{ formatIsoDate }}</p>
                <p v-if="notification.job.payload.platform">Format: {{ notification.job.payload.platform }}</p>
            </div>
        </template>
        <template #errorMessage>
            <p>There was an unexpected issue while preparing ads for download.</p>
            <template v-if="failedLabels.length">
                <p>The following ads could not be generated:</p>
                <ul>
                    <li v-for="label in failedLabels" :key="label">{{ label }}</li>
                </ul>
            </template>
        </template>
        <template #button>
            <Button size="small" target="_blank" data-testid="notification-item-download-icon" @click="downloadFile">
                <Icon type="md-download" />
            </Button>
        </template>
    </hox-notification-item>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { format } from "date-fns";
import nl2br from "hoxton-shared/utils/nl2br";

import { NotificationStatus } from "@/enums/notifications";
import { NotificationsAction, type Notification } from "@/store/modules/notifications";
import { fetchAndDownloadFile } from "@/utils/downloadFile";

export default defineComponent({
    props: {
        notification: {
            required: true,
            type: Object as PropType<Notification>
        },

        showAbortButton: {
            type: Boolean
        },

        isJobFinished: {
            type: Boolean,
            default: false
        }
    },

    emits: ["notificationDeleted"],

    data() {
        return {
            subProgressShowCount: 5,
            serviceTitles: {
                PsdDownloader: "PSD",
                ScreenshotDownloader: "image",
                VideoDownloader: "video"
            }
        };
    },

    computed: {
        hasFailed() {
            return this.notification.status === NotificationStatus.Failed;
        },

        failedLabels() {
            const {
                payload: { failedTasks }
            } = this.notification.job;

            if (!failedTasks) {
                return [];
            }

            return Object.values(failedTasks).map(({ label }) => label);
        },

        invisibleProgressCount() {
            const { payload } = this.notification.job;
            if (!payload.progress) {
                return 0;
            }

            return (
                Object.values(payload.progress).filter(prog => prog.percent < 100).length - this.subProgressShowCount
            );
        },

        isCompleted() {
            return this.notification.status === NotificationStatus.Completed;
        },

        notificationMessage() {
            return nl2br(this.notification.message);
        },

        showClearButton() {
            return !this.isJobFinished && (this.hasFailed || this.isCompleted);
        },

        statusText() {
            if (this.hasFailed) {
                return "Failed";
            }
            return this.notificationMessage;
        },

        subProgress() {
            const { payload } = this.notification.job;
            if (!payload.progress) {
                return null;
            }
            // We could sort here to put them in order of highest progress however this means that jobs
            // running at different speeds tend to jostle for the top 5 position and so the displayed
            // items switch around on every update
            return Object.values(payload.progress)
                .filter(prog => prog.percent < 100)
                .slice(0, this.subProgressShowCount);
        },

        isFacebookJob() {
            const {
                payload: { facebookData }
            } = this.notification.job;
            return facebookData != null;
        },

        title() {
            if (this.isFacebookJob) {
                return "Publish to Facebook Ads Manager";
            }

            const serviceName = this.notification.job.name as keyof typeof this.serviceTitles;

            if (serviceName === "PsdDownloader") {
                const jobContentType = this.notification.job.payload?.contentType;

                if (jobContentType === "png" || jobContentType === "jpeg") {
                    return `Download ${this.serviceTitles.ScreenshotDownloader} ads`;
                }
            }

            return `Download ${this.serviceTitles[serviceName] || "your"} ads`;
        },

        formatIsoDate() {
            return format(new Date(this.notification.job.payload.startedAt), "dd-MM-yyyy");
        }
    },

    methods: {
        removeNotification() {
            this.$store.dispatch(NotificationsAction.RemoveItem, this.notification.id);
        },

        deleteCompletedNotification() {
            this.$emit("notificationDeleted");
        },

        getLinkLabel(link: string) {
            const regex = /\/files\/(.*?)\?/;
            const match = link.match(regex);

            return match?.[1] ?? "Untitled file";
        },

        downloadFile() {
            const label = this.getLinkLabel(this.notification.downloadLink!);
            const response = fetchAndDownloadFile(this.notification.downloadLink!, label);

            if (!response) {
                this.$snackbar.error("Failed to download the file. Please try again later.");
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-job-notification-item__sub-progress {
    margin-top: $spacing-small;
    padding-left: $spacing-base;
    border-left: 1px solid $cmp-light-border-color;

    &-label {
        display: flex;
        justify-content: space-between;
        overflow: hidden;
        margin-bottom: $spacing-smaller;

        > :first-child {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}
</style>
